"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { clientLogger } from "@/lib/client-logger";
import { isValidEmail, isValidWaxAccount } from "@/lib/validators";

/**
 * Props for the ClaimForm component.
 */
export interface ClaimFormProps {
  clue: {
    id: string;
    title: string;
    assetMetadata: Record<string, unknown> | null;
  };
  campaign: {
    id: string;
    title: string;
    orgName: string;
  };
  tenant: {
    slug: string;
    name: string;
    primaryColor: string;
  };
  chipId: string;
  refId: string;
  deviceHash: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  waxAddress: string;
}

/**
 * Form component for claiming digital assets at scavenger hunt locations.
 *
 * Features:
 * - Collects user information (name, email, optional WAX address)
 * - Persists form data to localStorage for convenience across locations
 * - Validates email and WAX address formats
 * - Submits claim to /api/claims endpoint
 * - Shows already claimed state if user has previously claimed
 */
export function ClaimForm({ clue, campaign, tenant, chipId, refId, deviceHash }: ClaimFormProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    waxAddress: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Load saved form data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(`scavenger-hunt-${tenant.slug}`);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
      } catch (error) {
        clientLogger.error("Error parsing saved form data", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
      }
    }
  }, [tenant.slug]);

  // Save form data to localStorage on change
  useEffect(() => {
    if (formData.firstName || formData.lastName || formData.email || formData.waxAddress) {
      localStorage.setItem(`scavenger-hunt-${tenant.slug}`, JSON.stringify(formData));
    }
  }, [formData, tenant.slug]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Use centralized validators from @/lib/validators
  const validateWaxAddress = (address: string) => {
    // Optional field - empty is valid
    if (!address) return true;
    return isValidWaxAccount(address, 'strict');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setSubmitStatus({
        success: false,
        message: "Please fill in all required fields.",
      });
      setIsSubmitting(false);
      return;
    }

    if (!isValidEmail(formData.email)) {
      setSubmitStatus({
        success: false,
        message: "Please enter a valid email address.",
      });
      setIsSubmitting(false);
      return;
    }

    if (formData.waxAddress && !validateWaxAddress(formData.waxAddress)) {
      setSubmitStatus({
        success: false,
        message: "Please enter a valid WAX address (12 characters, lowercase letters and numbers 1-5).",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("/api/claims", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.toLowerCase().trim(),
          waxAddress: formData.waxAddress.trim() || null,
          clueId: clue.id,
          campaignId: campaign.id,
          chipId,
          refId,
          deviceHash,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to submit claim");
      }

      setSubmitStatus({
        success: true,
        message: result.message || "Success! Your digital asset is being minted. Check your wallet soon!",
      });

      // Mark this location as claimed in localStorage
      const claimedKey = `claimed-${tenant.slug}-${clue.id}`;
      localStorage.setItem(claimedKey, "true");

    } catch (error) {
      clientLogger.error("Error submitting claim", {
        error: error instanceof Error ? error.message : "Unknown error",
        clueId: clue.id
      });
      setSubmitStatus({
        success: false,
        message: error instanceof Error ? error.message : "An error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if already claimed
  const claimedKey = `claimed-${tenant.slug}-${clue.id}`;
  const alreadyClaimed = typeof window !== 'undefined' && localStorage.getItem(claimedKey) === "true";

  if (alreadyClaimed) {
    return (
      <div className="bg-green-50 rounded-lg border-l-4 border-green-400 p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">Already Claimed!</h3>
            <div className="mt-2 text-sm text-green-700">
              <p>You have already claimed the digital asset for this location. Visit the other locations to continue your scavenger hunt!</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex flex-col items-center mb-6">
        <div className="relative w-32 h-32 mb-4">
          {clue.assetMetadata?.image ? (
            <Image
              src={clue.assetMetadata.image as string}
              alt={`${clue.title} Digital Asset`}
              className="object-contain w-full h-full rounded-lg"
              fill
              unoptimized
            />
          ) : (
            <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center text-4xl">
              &#127873;
            </div>
          )}
        </div>
        <h3 className="text-xl font-bold text-gray-900 text-center">
          Claim Your {clue.title} Digital Asset
        </h3>
        <p className="text-gray-600 text-sm text-center mt-2">
          Fill out the form below to claim your exclusive digital reward
        </p>
      </div>

      {submitStatus ? (
        <div
          className={`p-4 rounded-md ${
            submitStatus.success ? "bg-green-50" : "bg-red-50"
          }`}
        >
          <p
            className={`text-sm ${
              submitStatus.success ? "text-green-800" : "text-red-800"
            }`}
          >
            {submitStatus.message}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <Label htmlFor="lastName">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              Please use the same email at each location.
            </p>
          </div>

          <div>
            <Label htmlFor="waxAddress">
              WAX Wallet Address (Optional)
            </Label>
            <Input
              type="text"
              id="waxAddress"
              name="waxAddress"
              value={formData.waxAddress}
              onChange={handleChange}
              placeholder="e.g., mywalletname"
              className="mt-1"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Receive your digital asset directly to your WAX wallet.
              If not provided, we&apos;ll create a custodial wallet for you.
            </p>
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
              style={{
                backgroundColor: tenant.primaryColor || undefined
              }}
            >
              {isSubmitting ? "Claiming..." : "Claim My Digital Asset"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
